<template>
  <section
    v-if="document"
    class="card px-3 is-radius-large p-t-10 p-b-10"
    :class="customClasses"
    :data-e2e="AICypressAttribute.views.CLARA_SOURCE_CARD"
  >
    <div class="is-flex is-align-items-center">
      <img
        v-if="document.sourceEnum?.icon"
        :src="document.sourceEnum.icon"
        class="source-icon"
        :data-e2e="AICypressAttribute.views.CLARA_SOURCE_ICON"
      />
      <span v-if="document.sourceEnum?.value" class="m-l-5 has-text-weight-semibold is-size-5">
        {{ document.sourceEnum.value }}
      </span>
    </div>
    <section class="source-content-container">
      <div
        v-if="document.title"
        class="is-size-5"
        :class="{ 'is-clickable': hasHover }"
        :data-e2e="AICypressAttribute.views.CLARA_SOURCE_TITLE"
      >
        <ClaraLegalReasoningSourceTitle
          v-if="document.isLegalReasoning && hasHover"
          :document="document"
          :source="source"
        />
        <div
          v-else-if="document.isContentLibrary && !document.url"
          class="has-text-primary has-text-weight-semibold"
          @click="onClickContentLibrarySource"
          :data-e2e="AICypressAttribute.views.CLARA_SOURCE_CONTENT_LIBRARY_TITLE"
        >
          {{ document.title }}
        </div>
        <a v-else class="has-text-primary has-text-weight-semibold" :href="url" target="_blank">
          {{ document.title }}
        </a>
      </div>
      <div
        class="clara-source-content has-text-grey m-t-5 m-b-5 overflow-hidden is-size-5 whitespace-pre-line"
      >
        <ClaraLegalReasoningSourceContent
          v-if="document.isLegalReasoning"
          :content="source.content"
          :lighter-text="true"
        />
        <template v-else>
          {{ source.content }}
        </template>
      </div>
    </section>
  </section>
</template>

<script>
import { AICypressAttribute } from '@/modules/ai/constants/AICypressAttribute';
import { EstateRagSimilarityDataObject } from '@/modules/ai/models/EstateRagSimilarityDataObject';
import { EstateRagDocumentObject } from '@/modules/ai/models/EstateRagDocumentObject';
import ClaraLegalReasoningSourceContent from '@/modules/ai/components/presentational/ClaraLegalReasoningSourceContent.vue';
import ClaraLegalReasoningSourceTitle from '@/modules/ai/components/presentational/ClaraLegalReasoningSourceTitle.vue';
import { AiRoute } from '@/modules/ai/routes/ai.routes';
import DocumentUIService from '@/modules/core/app/services/DocumentUIService';
import ContentSourceListService from '@/modules/ai/services/ContentSourceListService';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'ClaraSource',
  components: {
    ClaraLegalReasoningSourceContent,
    ClaraLegalReasoningSourceTitle
  },
  props: {
    document: {
      type: EstateRagDocumentObject,
      required: true
    },
    source: {
      type: EstateRagSimilarityDataObject
    },
    width: {
      type: String,
      default: 'unset'
    },
    hasBorder: {
      type: Boolean,
      default: true
    },
    hasHover: {
      type: Boolean,
      default: false
    }
  },
  static: {
    AICypressAttribute
  },
  data() {
    return {
      defaultClasses: []
    };
  },
  computed: {
    customClasses() {
      const classes = this.hasBorder
        ? ['has-border-light has-radius-large p-3', ...this.defaultClasses]
        : [];

      return this.hasHover ? [...classes, 'has-hover'] : classes;
    },
    url() {
      if (this.document.isNotion && this.source.blockId) {
        return `${this.document.url}#${this.source.parsedBlockId}`;
      }

      return this.document.url;
    }
  },
  methods: {
    async onClickContentLibrarySource() {
      const contentSource = (
        await ContentSourceListService.getRows({
          ragId: this.document.ragDocumentId
        })
      )[0];

      if (contentSource.usesPdfDocument && contentSource.contentSourceDocument?.url) {
        DocumentUIService.openUrl(contentSource.contentSourceDocument.url);
        return;
      }

      this.$router.push({
        name: AiRoute.CONTENT_LIBRARY_SOURCE.name,
        params: { id: contentSource.id }
      });
    }
  }
};
</script>

<style scoped>
.card {
  width: v-bind(width);
  transition: background 0.2s ease-out;
  .source-icon {
    height: var(--ce-18px);
  }

  &.has-hover {
    &:hover {
      background-color: var(--ce-primary-light);
    }
  }

  .source-content-container {
    height: var(--ce-82px);
    overflow: hidden;
    .clara-source-content {
      word-break: break-word;
      line-height: 1.2;
      height: var(--ce-50px) !important;
    }
  }
}
</style>
